import { AccountType } from '@/api/dealings/types'
import { FundID } from '@/api/funds/enum'
export { FundID, FundName } from '@/api/funds/enum'

// ─────────────────────────────────────────────────────────────────────────────
// 型別 / 介面
// ─────────────────────────────────────────────────────────────────────────────

type RuleFunction = (condition: any) => boolean

interface ICondition {
  sale_pid?: number;
  agent_pid?: number;
  holder_pid?: number;
  accountType?: AccountType;
}

interface RuleGroup {
  name: string;
  multipleTrades: boolean;
  /** 是否允許遞件時缺少必填檔案。 */
  allowMissingFilesOnDelivery?: boolean;
  amendRule: AmendRule;
  redeemMethod: RedeemMethod;
  subscribeFee: SubscribeFee;
  redeemFee: RedeemFee;
  rules: RuleFunction[];
  /** 未定義代表沿用一般投資人的 API 基金與金額限制。 */
  subscribableFunds?: SubscribableFund[];
}

interface SubscribableFund {
  fundID: string;
  initial: SubscribeLimit[];
  subsequent: SubscribeLimit[];
  /** 最低庫存規則需與後端 RedemptionMinimumBalance 同步；維護步驟見 doc/DEALING_MINIMUM_BALANCE_RULES.md。 */
  minBalance: SubscribeLimit[];
}

interface SubscribeLimit {
  minAmount: number;
  type: LimitType;
}

// ─────────────────────────────────────────────────────────────────────────────
// 列舉
// ─────────────────────────────────────────────────────────────────────────────

enum LimitType {
  Block = '把關',
  Warn = '提醒',
}

enum RedeemMethod {
  FIFO = '先進先出',
  Specific = '指定原申購日',
  SpecificMixed = '指定原申購日或DealingDate'
}

/**
 * 指定贖回庫存分組方式。
 */
enum SpecifiedRedeemGroupRule {
  /** Nominee 指定贖回：依原申購日 / inventory_month 分組。 */
  StockDate = 'stock_date',
  /** UG Pension / Bonus 指定贖回：依 Dealing Date 分組。 */
  DealingDate = 'dealing_date',
}

enum RedeemFee {
  Normal = '照收手續費',
  Waive = '不收手續費',
}

enum SubscribeFee {
  ZeroLoad = '固定不收認購手續費',
  ZeroRated = '預設帶入0，可做調整',
  DefaultOnePct = '預設帶入1%，可做調整',
}

enum AmendRule {
  NotAllowed = '不可改單',
  BeforeDealingDateNoon = '交易日 12:00 前',
  BeforeCutoffDateTwoPm = '結單日 14:00 前',
}

// ─────────────────────────────────────────────────────────────────────────────
// 資料結構
// ─────────────────────────────────────────────────────────────────────────────

const FundClassMap: Record<string, string[]> = {
  A: [FundID.SOF_A, FundID.GCMF_A, FundID.UGWAF_A],
  B: [FundID.SOF_B, FundID.GCMF_B, FundID.UGWAF_B],
  C: [FundID.SOF_C, FundID.GCMF_C, FundID.UGWAF_C],
  M: [FundID.SOF_M, FundID.GCMF_M, FundID.UGWAF_M],
}

function buildClassLimits(fundClass: 'A' | 'B' | 'C' | 'M', initial: SubscribeLimit[], subsequent: SubscribeLimit[], minBalance: SubscribeLimit[]): SubscribableFund[] {
  return FundClassMap[fundClass].map(fundID => ({ fundID, initial, subsequent, minBalance }))
}

// minBalance 的身份條件、基金範圍、Block/Warn 與 RuleGroups 優先順序變動時，須同步後端 Services/RedemptionMinimumBalance.cs 與兩邊測試。
const FundLimitSets = {
  ClassA_M: [
    ...buildClassLimits('A',
      [{ minAmount: 100000, type: LimitType.Block }],
      [{ minAmount: 10000, type: LimitType.Warn }],
      [{ minAmount: 10000, type: LimitType.Block }],
    ),
    ...buildClassLimits('M',
      [{ minAmount: 100000, type: LimitType.Block }],
      [{ minAmount: 10000, type: LimitType.Warn }],
      [{ minAmount: 10000, type: LimitType.Block }],
    ),
  ],
  ClassM: [
    ...buildClassLimits('M',
      [{ minAmount: 100000, type: LimitType.Block }],
      [{ minAmount: 10000, type: LimitType.Block }],
      [{ minAmount: 10000, type: LimitType.Block }],
    ),
  ],
  ClassA_B_COO: [
    // A
    ...buildClassLimits('A',
      [{ minAmount: 3000000, type: LimitType.Warn }, { minAmount: 100000, type: LimitType.Block }],
      [{ minAmount: 500000, type: LimitType.Warn }, { minAmount: 50000, type: LimitType.Block }],
      [{ minAmount: 500000, type: LimitType.Warn }, { minAmount: 50000, type: LimitType.Block }],
    ),
    // B
    ...buildClassLimits('B',
      [{ minAmount: 100000, type: LimitType.Block }],
      [{ minAmount: 50000, type: LimitType.Block }],
      [{ minAmount: 50000, type: LimitType.Block }],
    ),
  ],
  // 預設 A class，可調整至 B class
  ClassA_B: [
    ...buildClassLimits('A',
      [{ minAmount: 100000, type: LimitType.Block }],
      [{ minAmount: 50000, type: LimitType.Block }],
      [{ minAmount: 50000, type: LimitType.Block }],
    ),
    ...buildClassLimits('B',
      [{ minAmount: 100000, type: LimitType.Block }],
      [{ minAmount: 50000, type: LimitType.Block }],
      [{ minAmount: 50000, type: LimitType.Block }],
    ),
  ],
  ClassB: [
    ...buildClassLimits('B',
      [{ minAmount: 100000, type: LimitType.Block }],
      [{ minAmount: 50000, type: LimitType.Block }],
      [{ minAmount: 50000, type: LimitType.Block }],
    ),
  ],
}

// ─────────────────────────────────────────────────────────────────────────────
// 輔助工具
// ─────────────────────────────────────────────────────────────────────────────

function Condition(condition: ICondition) {
  return {
    includes(criteria: Partial<{ agent: number[]; sale: number[]; holder: number[]; accountType: AccountType }>): boolean {
      if (criteria.agent !== undefined) {
        if (condition.agent_pid === undefined || !criteria.agent.includes(condition.agent_pid)) return false
      }
      if (criteria.sale !== undefined) {
        if (condition.sale_pid === undefined || !criteria.sale.includes(condition.sale_pid)) return false
      }
      if (criteria.holder !== undefined) {
        if (condition.holder_pid === undefined || !criteria.holder.includes(condition.holder_pid)) return false
      }
      if (criteria.accountType !== undefined) {
        if (condition.accountType !== criteria.accountType) return false
      }
      return true
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 規則判斷函式 (Rows 1–8)
// ─────────────────────────────────────────────────────────────────────────────

function isUG(condition: ICondition) {
  return Condition(condition).includes({ agent: [35], sale: [60] })
}

function isPensionOrBonus(condition: ICondition) {
  return Condition(condition).includes({ agent: [35], sale: [107, 153] })
}

function isBossPensionBonus(condition: ICondition) {
  return Condition(condition).includes({ holder: [1235, 1254] }) && isPensionOrBonus(condition)
}

function isColleagueJointAccount(condition: ICondition) {
  return Condition(condition).includes({ accountType: AccountType.UGJoint })
}

function isUGRelated(condition: ICondition) {
  return Condition(condition).includes({ agent: [35], sale: [147] })
}

function isBossPersonal(condition: ICondition) {
  return Condition(condition).includes({ agent: [36], sale: [197] })
}

function isColleaguePersonal(condition: ICondition) {
  return Condition(condition).includes({ agent: [36] }) && !isBossPersonal(condition)
}

// ─────────────────────────────────────────────────────────────────────────────
// 規則判斷函式 (Rows 9–17)
// ─────────────────────────────────────────────────────────────────────────────

function isCOOAgent(condition: ICondition) {
  return Condition(condition).includes({ agent: [62], sale: [142] })
}

function isColleagueReferral(condition: ICondition) {
  return Condition(condition).includes({ agent: [51] })
}

function isSusanAgent(condition: ICondition) {
  return Condition(condition).includes({ agent: [54], sale: [109, 192] })
}

function isNominee(condition: ICondition) {
  return Condition(condition).includes({ accountType: AccountType.Nominee })
}

// Rows 10-15 合併條件（OR）
function isExternalChannel(condition: ICondition) {
  return (
    Condition(condition).includes({ agent: [77] }) ||                   // Tarobo
    Condition(condition).includes({ agent: [57] }) ||                   // Ex-Colleague
    Condition(condition).includes({ agent: [53], sale: [101, 102] }) || // E Family Referral
    Condition(condition).includes({ agent: [46], sale: [81] }) ||       // Institutional Investor
    Condition(condition).includes({ agent: [66], sale: [154] }) ||      // MKT
    Condition(condition).includes({ agent: [5], sale: [4] })            // Adwell
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// RuleGroups 清單
// ─────────────────────────────────────────────────────────────────────────────

const RuleGroups: RuleGroup[] = []

// ── Row 1: UG ────────────────────────────────────────────────────────────────
RuleGroups.push({
  name: "UG",
  multipleTrades: true,
  allowMissingFilesOnDelivery: true,
  amendRule: AmendRule.BeforeDealingDateNoon,
  subscribableFunds: FundLimitSets.ClassA_M,
  subscribeFee: SubscribeFee.ZeroLoad,
  redeemMethod: RedeemMethod.FIFO,
  redeemFee: RedeemFee.Waive,
  rules: [isUG]
})

// ── Row 4+5: Boss Pension Bonus (Wang Ming-Ching joint & personal) ─────────── (精確優先：holder + pension)
RuleGroups.push({
  name: "Boss Pension Bonus",
  multipleTrades: true,
  allowMissingFilesOnDelivery: true,
  amendRule: AmendRule.BeforeDealingDateNoon,
  subscribableFunds: FundLimitSets.ClassM,
  subscribeFee: SubscribeFee.ZeroLoad,
  redeemMethod: RedeemMethod.FIFO,
  redeemFee: RedeemFee.Normal,
  rules: [isBossPensionBonus]
})

// ── Row 3: Colleague Pension / Bonus (UG & Colleague joint account) ─────────── (精確優先：UGJoint + pension)
RuleGroups.push({
  name: "Colleague Pension / Bonus",
  multipleTrades: true,
  allowMissingFilesOnDelivery: true,
  amendRule: AmendRule.BeforeDealingDateNoon,
  subscribableFunds: FundLimitSets.ClassM,
  subscribeFee: SubscribeFee.ZeroLoad,
  redeemMethod: RedeemMethod.SpecificMixed,
  redeemFee: RedeemFee.Normal,
  rules: [isColleagueJointAccount, isPensionOrBonus]
})

// ── Row 2: Pension / Bonus (UG agent, Pension Bonus / Pension_3 sub-sales) ───
RuleGroups.push({
  name: "Pension / Bonus",
  multipleTrades: true,
  allowMissingFilesOnDelivery: true,
  amendRule: AmendRule.BeforeDealingDateNoon,
  subscribableFunds: FundLimitSets.ClassM,
  subscribeFee: SubscribeFee.ZeroLoad,
  redeemMethod: RedeemMethod.SpecificMixed,
  redeemFee: RedeemFee.Normal,
  rules: [isPensionOrBonus]
})

// ── Row 6: UG Related (Velvet Group) ─────────────────────────────────────────
RuleGroups.push({
  name: "UG Related",
  multipleTrades: true,
  allowMissingFilesOnDelivery: true,
  amendRule: AmendRule.BeforeDealingDateNoon,
  subscribableFunds: FundLimitSets.ClassA_M,
  subscribeFee: SubscribeFee.ZeroLoad,
  redeemMethod: RedeemMethod.FIFO,
  redeemFee: RedeemFee.Waive,
  rules: [isUGRelated]
})

// ── Row 7: Boss Personal (Colleague agent, Eugene sub-sales) ─────────────────
RuleGroups.push({
  name: "Boss Personal",
  multipleTrades: true,
  allowMissingFilesOnDelivery: true,
  amendRule: AmendRule.NotAllowed,
  subscribableFunds: FundLimitSets.ClassM,
  subscribeFee: SubscribeFee.ZeroLoad,
  redeemMethod: RedeemMethod.FIFO,
  redeemFee: RedeemFee.Waive,
  rules: [isBossPersonal]
})

// ── Row 8: Colleague Personal (Colleague agent, 同仁英文名 sub-sales) ──────────
RuleGroups.push({
  name: "Colleague Personal",
  multipleTrades: true,
  amendRule: AmendRule.NotAllowed,
  subscribableFunds: FundLimitSets.ClassM,
  subscribeFee: SubscribeFee.ZeroLoad,
  redeemMethod: RedeemMethod.FIFO,
  redeemFee: RedeemFee.Normal,
  rules: [isColleaguePersonal]
})

// ── Row 9: COO ────────────────────────────────────────────────────────────────
// 不一單多單；Switch 1.不設規則 2.特別轉換需提前溝通規則通知
RuleGroups.push({
  name: "COO",
  multipleTrades: false,
  amendRule: AmendRule.NotAllowed,
  subscribableFunds: FundLimitSets.ClassA_B_COO,
  subscribeFee: SubscribeFee.ZeroRated,
  redeemMethod: RedeemMethod.FIFO,
  redeemFee: RedeemFee.Normal,
  rules: [isCOOAgent]
})

// ── Rows 10-15: External Channel (Tarobo / Ex-Colleague / E Family Referral / Institutional / MKT / Adwell) ─
RuleGroups.push({
  name: "External Channel",
  multipleTrades: false,
  amendRule: AmendRule.NotAllowed,
  subscribableFunds: FundLimitSets.ClassA_B,
  subscribeFee: SubscribeFee.ZeroRated,
  redeemMethod: RedeemMethod.FIFO,
  redeemFee: RedeemFee.Normal,
  rules: [isExternalChannel]
})

// ── Row 16: Colleague Referral ────────────────────────────────────────────────
// 認購手續費預設帶入 1%，可做調整
RuleGroups.push({
  name: "Colleague Referral",
  multipleTrades: false,
  amendRule: AmendRule.NotAllowed,
  subscribableFunds: FundLimitSets.ClassB,
  subscribeFee: SubscribeFee.DefaultOnePct,
  redeemMethod: RedeemMethod.FIFO,
  redeemFee: RedeemFee.Normal,
  rules: [isColleagueReferral]
})

// ── Row 17: Susan (Sub-sales: Susan / Susan_no_rebate) ────────────────────────
RuleGroups.push({
  name: "Susan",
  multipleTrades: false,
  amendRule: AmendRule.NotAllowed,
  subscribableFunds: FundLimitSets.ClassB,
  subscribeFee: SubscribeFee.ZeroRated,
  redeemMethod: RedeemMethod.FIFO,
  redeemFee: RedeemFee.Normal,
  rules: [isSusanAgent]
})

// ── Row 18: Nominee ─────────────────────────────────────────────────────────
RuleGroups.push({
  name: "Nominee",
  multipleTrades: true,
  amendRule: AmendRule.BeforeCutoffDateTwoPm,
  subscribeFee: SubscribeFee.ZeroLoad,
  redeemMethod: RedeemMethod.Specific,
  redeemFee: RedeemFee.Normal,
  rules: [isNominee]
})

// ─────────────────────────────────────────────────────────────────────────────
// 查詢入口
// ─────────────────────────────────────────────────────────────────────────────

export type { RuleGroup, SubscribableFund, SubscribeLimit, ICondition }
export { AmendRule, LimitType, RedeemMethod, RedeemFee, SubscribeFee, SpecifiedRedeemGroupRule }

/**
 * 依帳戶條件查詢對應的 RuleGroup。
 * RuleGroup.rules 為 AND 邏輯（所有條件都符合才命中）；
 * 無 rules 的 group 視為預設 fallback，排最後。
 * 找不到任何符合時回傳 null。
 */
export function getRuleGroup(condition: ICondition): RuleGroup | null {
  return RuleGroups.find(group =>
    !group.rules || group.rules.every(rule => rule(condition))
  ) ?? null
}

/**
 * 查詢帳戶在指定基金的申購限制（initial / subsequent / minBalance）。
 * 找不到可申購的基金時回傳 null。
 */
export function getFundLimit(condition: ICondition, fundID: string): SubscribableFund | null {
  const group = getRuleGroup(condition)
  return group?.subscribableFunds?.find(f => f.fundID.toLowerCase() === fundID.toLowerCase()) ?? null
}

/**
 * 判斷目前條件是否允許一單多筆交易。
 * 沒命中特殊規則時維持一般規則，只允許單筆。
 */
export function canUseMultipleTrades(condition: ICondition): boolean {
  return getRuleGroup(condition)?.multipleTrades === true
}

/** 僅 internal rules Row 1–7 可在缺少必填檔案時先遞件。 */
export function canDeliverWithMissingFiles(condition: ICondition): boolean {
  return getRuleGroup(condition)?.allowMissingFilesOnDelivery === true
}

/**
 * 取得目前身份的改單期限規則；僅提供規則資料，實際日期與狀態判斷由改單流程處理。
 */
export function getAmendRule(condition: ICondition): AmendRule {
  return getRuleGroup(condition)?.amendRule ?? AmendRule.NotAllowed
}

/**
 * 判斷目前條件是否適用指定贖回。
 * 依前端規則表的 redeemMethod 判斷。
 */
export function canUseSpecifiedRedeem(condition: ICondition): boolean {
  return getSpecifiedRedeemGroupRule(condition) !== null
}

/**
 * 取得指定贖回的庫存分組方式。
 * Nominee 使用原申購日；UG Pension / Bonus 使用 Dealing Date。
 */
export function getSpecifiedRedeemGroupRule(condition: ICondition): SpecifiedRedeemGroupRule | null {
  const redeemMethod = getRuleGroup(condition)?.redeemMethod
  if (redeemMethod === RedeemMethod.Specific) return SpecifiedRedeemGroupRule.StockDate
  if (redeemMethod === RedeemMethod.SpecificMixed) return SpecifiedRedeemGroupRule.DealingDate
  return null
}

/**
 * 判斷目前條件是否不收贖回手續費。
 * 只看前端規則表，不讀後端特殊旗標。
 */
export function canWaiveRedeemFee(condition: ICondition): boolean {
  return getRuleGroup(condition)?.redeemFee === RedeemFee.Waive
}

/**
 * 取得目前條件的認購手續費預設值。
 * 回傳 null 代表沒有特殊規則，維持既有表單/API 預設值。
 */
export function getDefaultSubscribeFeeRate(condition: ICondition): number | null {
  const subscribeFee = getRuleGroup(condition)?.subscribeFee
  if (subscribeFee === SubscribeFee.ZeroLoad || subscribeFee === SubscribeFee.ZeroRated) return 0
  if (subscribeFee === SubscribeFee.DefaultOnePct) return 1
  return null
}

/**
 * 判斷認購手續費是否固定不收。
 * 固定不收時畫面應鎖定費率，不讓使用者改成其他值。
 */
export function isSubscribeFeeFixedZero(condition: ICondition): boolean {
  return getRuleGroup(condition)?.subscribeFee === SubscribeFee.ZeroLoad
}
